// A small dialog hung off the button that opened it, so the confirm lands under
// the pointer. ReasonPopover is the text-field case of the same idea; this one
// takes any body.

import React, { useRef, useState } from 'react';
import Tooltip from '../components/ui/Tooltip';
import Popover from '../components/ui/Popover';

// React 17: no useId.
let seq = 0;

export interface AnchoredPopoverProps {
  triggerLabel: React.ReactNode;
  triggerClassName: string;
  /** Hover text on the trigger, saying what happens. */
  tooltip?: string;
  /** Accessible name for the trigger when its label is not words. */
  triggerAriaLabel?: string;
  title: string;
  disabled?: boolean;
  /** Which edge of the trigger the panel lines up with on a wide screen. */
  align?: 'left' | 'right';
  /** sm, md or lg, the kit popover widths. */
  width?: 'sm' | 'md' | 'lg';
  /** Stretch the trigger to its container, for a trigger drawn as a field. */
  fullWidth?: boolean;
  onOpen?: () => void;
  children: (close: () => void) => React.ReactNode;
}

const AnchoredPopover: React.FC<AnchoredPopoverProps> = ({
  triggerLabel,
  triggerClassName,
  tooltip,
  triggerAriaLabel,
  title,
  disabled,
  align = 'right',
  width = 'md',
  fullWidth = false,
  onOpen,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const idRef = useRef('');
  if (!idRef.current) idRef.current = `anchored-popover-${++seq}`;
  const id = idRef.current;

  const close = () => setOpen(false);

  return (
    <div className={`relative ${fullWidth ? 'flex w-full [&>span]:w-full' : 'inline-flex'}`}>
      <Tooltip label={tooltip || ''}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-label={triggerAriaLabel}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          onClick={() => {
            if (open) close();
            else {
              setOpen(true);
              onOpen?.();
            }
          }}
          className={`${triggerClassName} ${fullWidth ? 'w-full' : ''}`}
        >
          {triggerLabel}
        </button>
      </Tooltip>
      <Popover
        open={open}
        onClose={close}
        anchor={triggerRef}
        id={id}
        title={title}
        width={width}
        align={align === 'right' ? 'end' : 'start'}
        hideClose
      >
        {children(close)}
      </Popover>
    </div>
  );
};

export default AnchoredPopover;
